// src/lib/options.ts
//
// Dropdown data. Every create/edit form needs the same handful of lists — companies,
// schemes, buildings, lettable units, vendors — so they are fetched once, cached for
// a minute and shared, rather than each modal running its own query.

import { useQuery } from '@tanstack/react-query';
import { api } from './api';

export type Option = Record<string, any>;

const options = (path: string, query: Record<string, unknown> = {}, enabled = true) => ({
  queryKey: ['options', path, query],
  queryFn: async () => (await api.get<Option[]>(path, query)).data,
  staleTime: 60_000,
  enabled,
});

/** Legal entities — the company a scheme, invoice or account belongs to. */
export const useEntityOptions = () => useQuery(options('/entities'));

export const usePropertyOptions = () => useQuery(options('/properties', { perPage: 200 }));

export const useBuildingOptions = (propertyId?: string) =>
  useQuery(options('/buildings', { propertyId, perPage: 100 }, !!propertyId));

/** Units that can carry a new advert or a new job. */
export const useUnitOptions = (filters: Record<string, unknown> = {}, enabled = true) =>
  useQuery(options('/units', { perPage: 200, ...filters }, enabled));

/** Everything currently advertised — what a viewing or an application attaches to. */
export const useListingOptions = (enabled = true) =>
  useQuery(options('/listings', { status: 'PUBLISHED', perPage: 200 }, enabled));

/** Open leads, for attaching a viewing or an application to the enquiry it came from. */
export const useLeadOptions = (enabled = true) =>
  useQuery(options('/leads', { perPage: 200 }, enabled));

/** Live tenancies on a unit — what a move-in or move-out inspection attaches to. */
export const useTenancyOptions = (unitId?: string) =>
  useQuery(options('/tenancies', { unitId, perPage: 50 }, !!unitId));

export const useVendorOptions = (enabled = true) => useQuery(options('/vendors', { perPage: 200 }, enabled));

export const useUserOptions = (enabled = true) => useQuery(options('/users', { perPage: 200, isActive: true }, enabled));

/** A scheme's initials, used to seed a unit reference prefix (Elgar House → EH). */
export const initialsOf = (name = '') =>
  name.split(/\s+/).filter(Boolean).map((w) => w[0]!.toUpperCase()).join('').slice(0, 3) || 'U';
